import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);
const RENEW_AFTER_MS = 12 * 60 * 60 * 1000;

// Tokens last 7 days. Swap in a fresh one when the current one is over 12 h
// old, so someone who keeps using Brainydo is never logged out by expiry —
// only by the inactivity setting (Settings → Security) or by logging out.
function renewTokenIfOld() {
  try {
    const token = localStorage.getItem("sw_token");
    const iat = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).iat;
    if (!iat || Date.now() - iat * 1000 < RENEW_AFTER_MS) return;
  } catch { return; }
  api.post("/auth/refresh")
    .then((r) => { if (r.data?.token) localStorage.setItem("sw_token", r.data.token); })
    .catch(() => {});
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sw_user") || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sw_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("sw_user", JSON.stringify(r.data));
        renewTokenIfOld();
      })
      .catch((err) => {
        // Stale/invalid token (e.g. signed before a JWT_SECRET rotation): drop the
        // cached user too, otherwise the app looks logged in but every call 403s.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem("sw_token");
          localStorage.removeItem("sw_user");
          setUser(null);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Store a session returned by any sign-in endpoint (password, code, one-time link).
  const acceptSession = (data) => {
    localStorage.setItem("sw_token", data.token);
    localStorage.setItem("sw_user", JSON.stringify(data.user));
    setUser(data.user);
    sessionStorage.removeItem("brainydo_reconnect_complete");
    api.get("/auth/me").then((r) => { setUser(r.data); localStorage.setItem("sw_user", JSON.stringify(r.data)); }).catch(() => {});
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("sw_token", data.token);
    localStorage.setItem("sw_user", JSON.stringify(data.user));
    setUser(data.user);
    // Reset the reconnect-complete flag so the overlay shows this login session
    sessionStorage.removeItem("brainydo_reconnect_complete");
    // Silent WhatsApp reconnect — fire and forget, no error shown to user
    api.post("/whatsapp/connect", {}).catch(() => {});
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("sw_token", data.token);
    localStorage.setItem("sw_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("sw_token");
    localStorage.removeItem("sw_user");
    setUser(null);
    window.location.href = "/login";
  };

  const refreshUser = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("sw_user", JSON.stringify(r.data));
    } catch { /* silent */ }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, acceptSession, register, logout, refreshUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
